import type { AssuranceMetricsV2 } from "@unbox/api";
import type { ComponentType, SVGProps } from "react";
import { useState } from "react";
import { useAssuranceOverviewV2 } from "@/api/assurance/assurance-overview";
import { SAFETY_POLICY_LABELS, SAFETY_POLICY_PRIORITY } from "@/api/safety-policies";
import { Skeleton } from "@/components/ui/skeleton";
import { useScopedUrl } from "@/hooks/use-scoped-url";
import { getStartDateISO } from "@/utils/date-conversion";
import { formatViews } from "@/utils/formatters";
import { BreakdownTile } from "./BreakdownTile";
import { CreamCard } from "./CreamCard";
import { SafetyEmptyState } from "./SafetyEmptyState";
import { SafetyGraph, type SafetyGraphItem } from "./SafetyGraph";
import { SectionDropdownLabel } from "./SectionDropdownLabel";
import { StatTile } from "./StatTile";

type SummaryCardProps = {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  variant: "risks" | "resolution";
};

const isRisksEmpty = (data: AssuranceMetricsV2) => data.totalFlags === 0 && data.totalVideos === 0;

const isResolutionEmpty = (data: AssuranceMetricsV2) =>
  data.risksResolvedCount === 0 &&
  data.videosAwaitingAction === 0 &&
  data.resolvedSplit.withCreator === 0 &&
  data.resolvedSplit.resolved === 0;

export const SummaryCard = ({ icon: Icon, title, variant }: SummaryCardProps) => {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useAssuranceOverviewV2({ startDate: getStartDateISO(days) });

  const isEmpty = data && (variant === "risks" ? isRisksEmpty(data) : isResolutionEmpty(data));

  return (
    <div className="flex flex-col gap-3 flex-1 min-w-0">
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-2">
          <Icon className="size-5 text-secondary" />
          <p className="text-base text-secondary font-medium">{title}</p>
        </div>
        <SectionDropdownLabel days={days} onChange={setDays} />
      </div>
      {isLoading || !data ? (
        <Skeleton className="h-[380px] w-full rounded-xl" />
      ) : isEmpty ? (
        <SafetyEmptyState
          title={variant === "risks" ? "No risks detected" : "No resolutions yet"}
          subtitle={
            variant === "risks"
              ? "No risky content has been analysed in the last 30 days."
              : "No risks have been resolved in the last 30 days."
          }
          width="100%"
          height="380px"
        />
      ) : variant === "risks" ? (
        <RisksCardBody data={data} days={days} />
      ) : (
        <ResolutionCardBody data={data} />
      )}
    </div>
  );
};

const RisksCardBody = ({ data, days }: { data: AssuranceMetricsV2; days: number }) => {
  const scopedUrl = useScopedUrl();

  const chartData: SafetyGraphItem[] = data.topFlagRiskTypes.map((risk) => ({
    label: risk.riskTypeShort,
    value: risk.count,
    link: risk.riskId
      ? {
          to: `${scopedUrl}/flagged-videos` as string,
          search: {
            tab: "needs-review",
            riskId: [risk.riskId],
            flaggedDateStart: getStartDateISO(days),
            flaggedDateEnd: getStartDateISO(0),
          },
        }
      : undefined,
  }));

  return (
    <CreamCard className="flex flex-col gap-8">
      <div className="flex gap-3 w-full items-stretch">
        <StatTile value={formatViews(data.totalFlags)} label="Risks detected" />
        <StatTile value={formatViews(data.totalVideos)} label="Total videos analysed" />
        <BreakdownTile
          title="Risks"
          rows={[
            { label: "Confirmed", value: data.flagsWithAddFlagAction },
            { label: "Dismissed", value: data.flagsWithDismissedAction },
          ]}
          donutData={[{ value: data.flagsWithAddFlagAction }, { value: data.flagsWithDismissedAction }]}
          donutColors={["#0BA5EC", "#717680"]}
        />
      </div>
      <div className="flex flex-col gap-4 w-full">
        <p className="text-sm text-secondary">Top 5 Risk Types</p>
        <SafetyGraph data={chartData} />
      </div>
    </CreamCard>
  );
};

const TOP_RESOLUTIONS_ROW_COUNT = 5;

const ResolutionCardBody = ({ data }: { data: AssuranceMetricsV2 }) => {
  const realEntries: SafetyGraphItem[] = data.topResolutionsByPolicy.map((resolution) => ({
    label: SAFETY_POLICY_LABELS[resolution.policyType as keyof typeof SAFETY_POLICY_LABELS] ?? resolution.policyType,
    value: resolution.count,
  }));

  // Pad remaining slots (up to 5) with the highest-priority policies not already
  // in the real data, at 0 — so the row still shows the policy's name instead of
  // a blank placeholder bar.
  const presentTypes = new Set(data.topResolutionsByPolicy.map((r) => r.policyType));
  const missingSlots = TOP_RESOLUTIONS_ROW_COUNT - realEntries.length;
  const paddedEntries: SafetyGraphItem[] =
    missingSlots > 0
      ? (Object.keys(SAFETY_POLICY_PRIORITY) as (keyof typeof SAFETY_POLICY_PRIORITY)[])
          .filter((type) => !presentTypes.has(type))
          .sort((a, b) => SAFETY_POLICY_PRIORITY[a] - SAFETY_POLICY_PRIORITY[b])
          .slice(0, missingSlots)
          .map((type) => ({ label: SAFETY_POLICY_LABELS[type], value: 0 }))
      : [];

  const chartData: SafetyGraphItem[] = [...realEntries, ...paddedEntries];

  return (
    <CreamCard className="flex flex-col gap-8">
      <div className="flex gap-3 w-full items-stretch">
        <StatTile value={formatViews(data.risksResolvedCount)} label="Risks resolved" />
        <StatTile value={formatViews(data.videosAwaitingAction)} label="Awaiting creator action" />
        <BreakdownTile
          title="Resolved"
          rows={[
            { label: "With Creator", value: data.resolvedSplit.withCreator },
            { label: "Resolved", value: data.resolvedSplit.resolved },
          ]}
          donutData={[{ value: data.resolvedSplit.withCreator }, { value: data.resolvedSplit.resolved }]}
          donutColors={["#7A5AF8", "#16B364"]}
        />
      </div>
      <div className="flex flex-col gap-4 w-full">
        <p className="text-sm text-secondary">Top 5 resolutions by policy</p>
        <SafetyGraph data={chartData} />
      </div>
    </CreamCard>
  );
};
